import { Report, ReportConfiguration, ReportTemplate } from "../../../shared/reporting/api/biClient.types";
import {
  isChartReportType,
  isPivotReportType,
  isTabularReportType,
  isTileReportType,
} from "../../../shared/reporting/utils/reportTypeHelper";
import { createChartReportConfiguration } from "../../components/builder/utils/configuration/chartConfiguration";
import { createPivotReportConfiguration } from "../../components/builder/utils/configuration/pivotConfigurations";
import { createTabularReportConfiguration } from "../../components/builder/utils/configuration/tabularConfiguration";
import { createTileReportConfiguration } from "../../components/builder/utils/configuration/tileConfiguration";
import { createTypedAsyncThunk } from "../utils/createTypedAsyncThunk";
import { createCurrentReportAction, createFieldsStateAction, createMetaDataAction } from "../utils/storeUtils";
import { fieldsStateActions } from "./fieldStatesThunks";
import { changeDataSet } from "./metaDataThunks";

export const getReportConfiguration = createTypedAsyncThunk<ReportConfiguration | undefined>(
  "store/getReportConfiguration",
  (_, { getState }) => {
    const state = getState();
    const reportType = state.fieldsState.fieldsStateReducer.reportType;

    if (!reportType) {
      return;
    }
    if (isTabularReportType(reportType)) {
      return createTabularReportConfiguration(
        state.fieldsState.tabularFieldsReducer.conditions,
        state.fieldsState.tabularFieldsReducer.fields,
        state.fieldsState.tabularFieldsReducer.sorts,
        state.fieldsState.tabularFieldsReducer.grouping,
        state.fieldsState.tabularFieldsReducer.settings
      );
    }

    if (isPivotReportType(reportType)) {
      return createPivotReportConfiguration(
        state.fieldsState.pivotFieldsReducer.conditions,
        state.fieldsState.pivotFieldsReducer.rows,
        state.fieldsState.pivotFieldsReducer.columns,
        state.fieldsState.pivotFieldsReducer.values,
        state.fieldsState.pivotFieldsReducer.sorts,
        state.fieldsState.pivotFieldsReducer.settings
      );
    }

    if (isChartReportType(reportType)) {
      return createChartReportConfiguration(
        state.fieldsState.chartFieldsReducer.conditions,
        state.fieldsState.chartFieldsReducer.arguments,
        state.fieldsState.chartFieldsReducer.values,
        state.fieldsState.chartFieldsReducer.sorts,
        state.fieldsState.chartFieldsReducer.settings
      );
    }

    if (isTileReportType(reportType)) {
      return createTileReportConfiguration(
        state.fieldsState.tileFieldsReducer.conditions,
        state.fieldsState.tileFieldsReducer.fields,
        state.fieldsState.tileFieldsReducer.settings
      );
    }

    throw new Error(`Unsupported report type: ${reportType}`);
  }
);

export const setEditReport = createTypedAsyncThunk("store/setEditReport", (report: Report, { dispatch, getState }) => {
  const state = getState();
  const newDataSetId = report.dataSetId || state.metaData.dataSets[0]?.dataSetId;
  const dataSetNeedsUpdate = newDataSetId && newDataSetId !== state.metaData.dataSetId;

  if (dataSetNeedsUpdate) {
    dispatch(changeDataSet(newDataSetId));
  }

  dispatch(createCurrentReportAction("setCurrentReport", report));
  //sets setFieldStatesConfiguration
  dispatch(
    fieldsStateActions.initializeFieldsState({
      configuration: report.configuration,
      reportType: report.reportType,
    })
  );
  dispatch(createMetaDataAction("updateReportType", report.reportType));
});

export const setViewReport = createTypedAsyncThunk("store/setViewReport", (report: Report, { dispatch, getState }) => {
  const state = getState();

  const newDataSetId = report.dataSetId || state.metaData.dataSets[0]?.dataSetId;
  const dataSetNeedsUpdate = newDataSetId && newDataSetId !== state.metaData.dataSetId;

  if (dataSetNeedsUpdate) {
    dispatch(changeDataSet(newDataSetId));
  }
  dispatch(createFieldsStateAction("setReportType", report.reportType));
  dispatch(createCurrentReportAction("refreshSession"));
  dispatch(createCurrentReportAction("setCurrentReport", report));
  dispatch(createMetaDataAction("updateReportType", report.reportType));
});

export const applyTemplate = createTypedAsyncThunk(
  "store/applyTemplate",
  (template: ReportTemplate, { dispatch, getState }) => {
    const state = getState();
    const newDataSetId = template.dataSetId ?? state.metaData.dataSets[0]?.dataSetId;

    const dataSetNeedsUpdate = newDataSetId && newDataSetId !== state.metaData.dataSetId;
    if (dataSetNeedsUpdate) {
      dispatch(changeDataSet(newDataSetId));
    }

    dispatch(createCurrentReportAction("applyTemplateOnReport", template));

    dispatch(
      fieldsStateActions.initializeFieldsState({
        configuration: template.configuration,
        reportType: template.reportType,
      })
    );
    dispatch(createMetaDataAction("updateReportType", template.reportType));
  }
);

export const updateReport = createTypedAsyncThunk(
  "store/updateReport",
  (changes: Partial<Report | ReportTemplate>, { dispatch }) => {
    dispatch(createCurrentReportAction("updateReport", changes));

    if (changes.reportType) {
      dispatch(createMetaDataAction("updateReportType", changes.reportType));
    }
  }
);
